// Centralised auth for backend calls (#63).
//
// After the C1 hardening (#58), the backend derives the user ONLY from the JWT in
// the Authorization header. Every user-scoped call must therefore carry it — and
// #58 fixed the calls that had forgotten it. This module is the *safety net* so a
// FUTURE call can't reintroduce the bug:
//   • installAxiosAuthInterceptor() — every axios request to our backend gets the
//     JWT automatically (unless one is already set explicitly).
//   • authedFetch(url, opts)        — a drop-in fetch() that does the same.
//
// We attach the token ONLY to our own hosts (Lambda URLs / API Gateway / Supabase /
// app.sonmi.io) — never third-party URLs, so the token can't leak.
import axios from 'axios';
import { supabase } from '../supabase';

const AUTH_HOST_MARKERS = ['lambda-url', 'execute-api', 'supabase.co', 'sonmi.io'];

function isOurBackend(url) {
  return typeof url === 'string' && AUTH_HOST_MARKERS.some((h) => url.includes(h));
}

function hasAuthHeader(headers) {
  if (!headers) return false;
  // Support plain objects and axios AxiosHeaders.
  return !!(headers.Authorization || headers.authorization ||
    (typeof headers.get === 'function' && headers.get('Authorization')));
}

// Current Supabase access token. Registered AND anonymous/guest users both carry a
// real signed JWT, so this works for guests too. Returns null if there's no session.
export async function getAccessToken() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token || null;
  } catch (_) {
    return null;
  }
}

// Drop-in fetch() that always carries the JWT for our backend. Prefer this over a
// bare fetch() for any user-scoped backend call.
export async function authedFetch(url, options = {}) {
  const headers = { ...(options.headers || {}) };
  if (isOurBackend(url) && !hasAuthHeader(headers)) {
    const token = await getAccessToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  return fetch(url, { ...options, headers });
}

// Install once at app startup. Every axios request to our backend then gets the JWT
// automatically (unless the call already set one), so a new axios.post can't ship
// without the user's token.
let _installed = false;
export function installAxiosAuthInterceptor() {
  if (_installed) return;
  _installed = true;
  axios.interceptors.request.use(async (config) => {
    if (isOurBackend(config.url) && !hasAuthHeader(config.headers)) {
      const token = await getAccessToken();
      if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
}
